// src/modules/app-views/leads/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import LeadsInit from './pages/Init';
import ListProvider from 'components/core/ListProvider';
import { API } from 'constants/Api';
import leadsInitActionButton from 'modules/app-views/leads/pages/Init/actions/actionButton';
import leadsInitMenuButton from 'modules/app-views/leads/pages/Init/actions/menuButton';
import LeadsParametersModal from 'modules/app-views/leads/pages/Init/modals/LeadsParametersModal';
import DocumentProvider from 'components/core/DocumentProvider';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import LeadsDocument from 'modules/app-views/leads/pages/Document';
import leadsDocumentMenuButton from 'modules/app-views/leads/pages/Document/actions/menuButton';
import CardHeaderLeadsCol1 from 'modules/app-views/leads/components/CardHeaderLeadsCol1';
import CardHeaderLeadsCol2 from 'modules/app-views/leads/components/CardHeaderLeadsCol2';
import CardHeaderLeadsCol3 from 'modules/app-views/leads/components/CardHeaderLeadsCol3';
import CardHeaderLeadsTags from 'modules/app-views/leads/components/CardHeaderLeadsTags';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'leads';

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: LeadsParametersModal,
  parametersSeed: null,

  // ───────────────────────────────────────────────────────────────────────
  // MIGRATORY
  // ───────────────────────────────────────────────────────────────────────
  migratory: {
    title: null,
    schema: ['uniqueKey', 'name', 'phone', 'email', 'interest'],
  },
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'list',
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <LeadsInit />
      </ListProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.LEADS.PAGINATION,
      params: null,
      query: null,

      // data transformation
      returnAsObject: false,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      responseDoc: null,
      responseData: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: null,
      paramKey: null,
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [{ field: '_metadata.audit.updatedAt', order: 'desc', index: 1 }],
      search: {},
      textSearch: '',
      filter: {},
      drawer: { folder: 'inbox' },
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: {
      actions: ['view', 'archive', 'remove'],
      hiddenColumns: ['documentCode', 'distribution', 'assignment', 'createdAt'],
    },

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: null,

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'default',
      actions: ['filter', 'refresh', leadsInitActionButton, leadsInitMenuButton],

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },
    },
  },

  document: {
    highSecurity: false,

    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <LeadsDocument />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.LEADS.URL,
      params: null,
      query: null,

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/:_id',
      paramKey: '_id',
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: null,

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: null,

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: 'record',
      initialValues: { status: 'available' },
      actions: [leadsDocumentMenuButton(), 'return'],
      viewMode: 'view',
      backPage: 'init',
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card',

      avatar: {
        visible: false,
        placeholder: 'lead',
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },

      card: {
        title: props => <CardHeaderTitle {...props} name={toPascalCase(props?.name)} />,
        subtitle: props => (props?.interest ? <span>{props.interest?.toUpperCase()}</span> : null),

        column1: props => <CardHeaderLeadsCol1 {...props} forceShow />,
        column2: props => <CardHeaderLeadsCol2 {...props} forceShow />,
        column3: props => <CardHeaderLeadsCol3 {...props} forceShow />,

        tags: props => <CardHeaderLeadsTags {...props} />,
      },
    },
  },
};

// *******************************************************************************
// MOCK DATA
// *******************************************************************************
const MOCK = {
  document: {},
};

// *******************************************************************************
// MOCK DATA
// *******************************************************************************
const IMP = {
  document: {},
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, MOCK, preConfigChanges };

// *******************************************************************************
// HELPERS
// *******************************************************************************
function toPascalCase(str) {
  if (!str) return str;
  return str.toLowerCase().replace(/(?:^|\s)\S/g, char => char.toUpperCase());
}
